/**
 * 冻结账户 — 独立菜单页（冻结后不吃单、不发奖励、不能登录）
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

type UserDetail = {
  id: number;
  username: string;
  status: string;
  node_status?: string;
  php_status?: 'active' | 'frozen' | null;
  php_freeze?: string | null;
  frozen_reason: string | null;
  level_code: string;
};

export function FreezeAccountPage() {
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canFreeze = hasNode('platform.user.freeze');
  const [lookupInput, setLookupInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState<UserDetail | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<{ reason: string }>();
  const [renameForm] = Form.useForm<{ new_username: string }>();
  const [lineForm] = Form.useForm<{ reason: string }>();

  async function lookupUser() {
    const input = lookupInput.trim();
    if (!input) {
      message.warning('请输入用户名、用户ID或编号');
      return;
    }
    const query = /^\d+$/.test(input) ? { id: Number(input) } : { username: input };
    setLoading(true);
    try {
      const found = await api.get<{ id: number; username: string }>(
        '/api/admin/users/lookup',
        query,
      );
      const detail = await api.get<UserDetail>(`/api/admin/users/${found.id}`);
      setTarget(detail);
      form.resetFields();
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '用户未找到');
      setTarget(null);
    } finally {
      setLoading(false);
    }
  }

  async function submitFreeze(values: { reason: string }) {
    if (!target) return;
    setSubmitting(true);
    try {
      await api.post(`/api/admin/users/${target.id}/freeze`, { reason: values.reason });
      message.success('账户已冻结');
      const detail = await api.get<UserDetail>(`/api/admin/users/${target.id}`);
      setTarget(detail);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '冻结失败');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitFreezeLine(values: { reason: string }) {
    if (!target) return;
    setSubmitting(true);
    try {
      const res = await api.post<{ frozen_count: number }>(
        `/api/admin/users/${target.id}/freeze-line`,
        { reason: values.reason },
      );
      message.success(`已冻结推荐线 ${res.frozen_count} 个账户`);
      const detail = await api.get<UserDetail>(`/api/admin/users/${target.id}`);
      setTarget(detail);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '冻线失败');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitRename(values: { new_username: string }) {
    if (!target) return;
    setSubmitting(true);
    try {
      const res = await api.post<{ username: string }>(`/api/admin/users/${target.id}/rename`, {
        new_username: values.new_username,
      });
      message.success(`已改名为 ${res.username}`);
      const detail = await api.get<UserDetail>(`/api/admin/users/${target.id}`);
      setTarget(detail);
      renameForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '改名失败');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitUnfreeze() {
    if (!target) return;
    setSubmitting(true);
    try {
      await api.post(`/api/admin/users/${target.id}/unfreeze`);
      message.success('账户已解冻');
      const detail = await api.get<UserDetail>(`/api/admin/users/${target.id}`);
      setTarget(detail);
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '解冻失败');
    } finally {
      setSubmitting(false);
    }
  }

  if (!canFreeze) {
    return (
      <Alert
        type="error"
        showIcon
        message="无权限"
        description="需要 platform.user.freeze 节点权限。"
      />
    );
  }

  const isFrozen = target?.status === 'frozen';

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>冻结账户</h2>
      <Typography.Paragraph type="secondary">
        冻结后主账户不能登录、不能吃单（EP/OTC/注册排队）、不能获得静/动态奖励；子账户同理。解冻后恢复正常。
        也可「冻整条推荐线」或改登录名（双写 Node + PHP）。
      </Typography.Paragraph>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, maxWidth: 720 }}
        message="冻结效果"
        description={
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            <li>不能登录（含刷新 token）</li>
            <li>不能吃单 — EP 接盘/挂卖、OTC 下单、注册排队匹配均拒绝</li>
            <li>不发奖励 — 静态奖、平衡奖、分享奖等跳过该账户</li>
          </ul>
        }
      />

      <Card style={{ marginBottom: 16, maxWidth: 520 }}>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="用户名、用户ID或编号（如106060）"
            value={lookupInput}
            onChange={(e) => setLookupInput(e.target.value)}
            onPressEnter={() => void lookupUser()}
            disabled={loading}
          />
          <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={() => void lookupUser()}>
            查找
          </Button>
        </Space.Compact>
      </Card>

      {loading && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      )}

      {!loading && target && (
        <Card title={`#${target.id} ${target.username}`} style={{ maxWidth: 520 }}>
          <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="等级">{target.level_code}</Descriptions.Item>
            <Descriptions.Item label="状态">
              {isFrozen ? <Tag color="red">已冻结</Tag> : <Tag color="green">正常</Tag>}
            </Descriptions.Item>
            {target.php_status === 'frozen' && (
              <Descriptions.Item label="PHP 冻结">
                {target.php_freeze || 'frozen'}
              </Descriptions.Item>
            )}
            {target.frozen_reason && (
              <Descriptions.Item label="冻结原因">{target.frozen_reason}</Descriptions.Item>
            )}
          </Descriptions>

          {isFrozen ? (
            <Popconfirm
              title="确认解冻该账户？"
              onConfirm={() => void submitUnfreeze()}
              okText="解冻"
              cancelText="取消"
            >
              <Button loading={submitting}>解冻账户</Button>
            </Popconfirm>
          ) : (
            <Form form={form} layout="vertical" onFinish={submitFreeze}>
              <Form.Item
                name="reason"
                label="冻结原因"
                rules={[{ required: true, min: 3, message: '请填写至少 3 个字的冻结原因' }]}
              >
                <Input.TextArea rows={3} placeholder="必填，将写入用户记录并同步 PHP 冻结状态" />
              </Form.Item>
              <Space wrap>
                <Popconfirm
                  title="确认冻结该账户？冻结后不能登录、不能吃单、不能发奖励。"
                  onConfirm={() => form.submit()}
                  okText="冻结"
                  okButtonProps={{ danger: true }}
                  cancelText="取消"
                >
                  <Button type="primary" danger loading={submitting}>
                    冻结账户
                  </Button>
                </Popconfirm>
              </Space>
            </Form>
          )}

          <Form
            form={lineForm}
            layout="vertical"
            style={{ marginTop: 24 }}
            onFinish={submitFreezeLine}
          >
            <Form.Item
              name="reason"
              label="冻整条推荐线（含本账户及全部下级）"
              rules={[{ required: true, min: 3, message: '请填写至少 3 个字的冻结原因' }]}
            >
              <Input.TextArea rows={2} placeholder="原因将写入每个被冻账户" />
            </Form.Item>
            <Popconfirm
              title="确认冻结整条推荐线下所有账户？此操作影响面大。"
              onConfirm={() => lineForm.submit()}
              okText="冻整线"
              okButtonProps={{ danger: true }}
              cancelText="取消"
            >
              <Button danger loading={submitting}>
                冻整条推荐线
              </Button>
            </Popconfirm>
          </Form>

          <Form
            form={renameForm}
            layout="vertical"
            style={{ marginTop: 24 }}
            onFinish={submitRename}
          >
            <Form.Item
              name="new_username"
              label="改登录名（Node + PHP）"
              rules={[
                { required: true, min: 3, message: '至少 3 位' },
                { pattern: /^[a-zA-Z0-9_]+$/, message: '仅字母数字下划线' },
              ]}
            >
              <Input placeholder="新用户名" />
            </Form.Item>
            <Popconfirm
              title="确认修改登录名？将双写 Node users.username 与 PHP account。"
              onConfirm={() => renameForm.submit()}
              okText="改名"
              cancelText="取消"
            >
              <Button loading={submitting}>改登录名</Button>
            </Popconfirm>
          </Form>
        </Card>
      )}
    </div>
  );
}
